import { Point2D } from '@paragon/geometry-library';
import { calculateMemberPolygon } from '@paragon/three-shared';

import { convertFromDesignToLayoutCoordinates } from '../../library/layout-geometry/coordinates';
import type { RootState } from '../../store';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getSelectedItemEntries } from '../../store/selectors/getSelectedItemSelector';
import { isLayoutViewActive } from '../../store/selectors/layoutModeSelectors';
import { threeJsDom } from '../../three/util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../../three/util/events/types';
import { askToPoint, finishPointing } from '../assistantStore';
import { describeSelectedItem } from '../snapshot';
import { getTrussMembers, requireString, roundPoint, waitForAppToSettle } from './helpers';
import { defineTool, ToolError } from './types';

// How far from a click an analysis joint still counts as the one clicked, in inches.
const JOINT_PICK_RADIUS_IN_INCHES = 4;

/**
 * Waits for the designer's next click in the view, or `null` if they cancel or the chat stops.
 */
function waitForClick(chatId: string, question: string) {
  return new Promise<GlobalThreeJSDomObjectEvent | null>((resolve) => {
    const finish = (event: GlobalThreeJSDomObjectEvent | null) => {
      threeJsDom.removeGlobalListener('click', onClick);
      finishPointing(chatId);
      resolve(event);
    };
    // Lets the click through, so the app selects what was clicked as it always does.
    const onClick = (event: GlobalThreeJSDomObjectEvent) => finish(event);
    threeJsDom.addGlobalListener('click', onClick);
    askToPoint(chatId, question, () => finish(null));
  });
}

/**
 * What a click on the open truss landed on, worked out from where it was rather than from the selection, which a click
 * on empty space may leave as it was.
 */
function describeTrussClick(state: RootState, at: { x: number; y: number }) {
  const componentDesign = getActiveComponentDesign(state);
  if (componentDesign == null) return {};
  const component = componentDesign.component;
  const point = Point2D.fromObject(at);

  const members = getTrussMembers(componentDesign)
    .filter((member) => {
      try {
        return calculateMemberPolygon(member, component)?.containsPoint(point) ?? false;
      } catch {
        return false;
      }
    })
    .map((member) => member.name);

  let joint: { name: string; distance: number } | null = null;
  for (const candidate of componentDesign.analogModel?.analogJoints ?? []) {
    const distance = Math.hypot(candidate.x - at.x, candidate.y - at.y);
    if (distance <= JOINT_PICK_RADIUS_IN_INCHES && (joint == null || distance < joint.distance))
      joint = { name: `Joint ${candidate.name}`, distance };
  }

  return { truss: component.name, onMembers: members, ...(joint ? { nearJoint: joint.name } : {}) };
}

export const pointingTools = [
  defineTool<{ question: string }>({
    name: 'ask_designer_to_point',
    description:
      'Asks the designer to click something in the view, such as the joint or web they mean, and waits for the click. ' +
      'Use it when "this", "here", or a description could mean more than one thing and nothing useful is selected. ' +
      'Only for finding out what they mean in the view: never to ask for approval or a yes or no, which `propose_plan` ' +
      'and a reply do. ' +
      'Returns where they clicked (truss coordinates in the truss editor, plan coordinates in the layout), the members ' +
      'and nearest joint under the click on a truss, and what the click selected.',
    inputSchema: {
      type: 'object',
      properties: {
        question: {
          type: 'string',
          description: 'Shown to the designer, such as "Click the joint where the point load goes."',
        },
      },
      required: ['question'],
    },
    changesProject: false,
    async execute(input, { getState, chatId }) {
      if (chatId == null) throw new ToolError('Only the Assistant’s chat can ask the designer to point.');

      const click = await waitForClick(chatId, requireString(input, 'question'));
      if (click == null) throw new ToolError('The designer didn’t click anything.');

      // The app selects what was clicked once the click has gone through.
      await new Promise((resolve) => setTimeout(resolve, 50));
      await waitForAppToSettle(getState);
      const state = getState();
      const inLayout = isLayoutViewActive(state);
      const { worldPosition } = click.eventPosition;
      const at = roundPoint(inLayout ? convertFromDesignToLayoutCoordinates(worldPosition) : worldPosition);
      const component = getActiveComponentDesign(state)?.component ?? null;

      return {
        summary: {
          view: inLayout ? 'layout' : 'truss editor',
          at,
          ...(inLayout ? {} : describeTrussClick(state, at)),
          selected: getSelectedItemEntries(state).map((entry) =>
            describeSelectedItem(entry, inLayout ? null : component, state),
          ),
        },
      };
    },
  }),
];
